import { useState, useEffect } from 'react';
import { X, Pencil, Plus, Trash2, User, Truck, Ban, ShieldCheck } from 'lucide-react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import ContactPersonFormModal from './ContactPersonFormModal';

const STATUS_STYLES = {
  Available:  'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30',
  Booked:     'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30',
  'In Transit': 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  Delivered:  'text-teal-700 dark:text-teal-400 bg-teal-500/15 dark:bg-teal-500/10 border-teal-500/40 dark:border-teal-500/30',
  Invoiced:   'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30',
  Claim:      'text-red-700 dark:text-red-400 bg-red-500/15 dark:bg-red-500/10 border-red-500/40 dark:border-red-500/30',
};

const HISTORY_DISPLAY_LIMIT = 15;

export default function ContactViewModal({ contact, contacts = [], onClose, onEdit, onContactsChanged }) {
  const { staffRole } = useAuth();
  const [showPersonForm, setShowPersonForm] = useState(false);
  const [editPerson, setEditPerson] = useState(null);
  const [loadHistory, setLoadHistory] = useState(null); // null = loading
  const [blacklisting, setBlacklisting] = useState(false);
  // Reads back from the live `contacts` prop (refreshed by
  // onContactsChanged) instead of the possibly-stale `contact` prop
  // snapshot, same as `people` below -- so toggling blacklist updates
  // this modal in place instead of needing a close/reopen.
  const liveContact = contacts.find(c => c.id === contact.id) || contact;
  const people = contacts.filter(c => c.parent_contact_id === contact.id);

  const handleToggleBlacklist = async () => {
    const next = !liveContact.is_blacklisted;
    if (next && !window.confirm(`Blacklist ${liveContact.name}? Dispatchers will see a warning when booking loads with this account.`)) return;
    setBlacklisting(true);
    try {
      await client.entities.Contact.update(liveContact.id, { is_blacklisted: next });
      toast.success(next ? `${liveContact.name} blacklisted.` : `${liveContact.name} removed from blacklist.`);
      onContactsChanged?.();
    } finally {
      setBlacklisting(false);
    }
  };

  // Loads booked with this account, across whichever role it played
  // (shipper/consignee/customer). Filtered client-side over the
  // carrier's own loads -- same pattern the rest of the app already
  // uses (e.g. ActiveLoad.jsx) rather than a scoped server query, since
  // there's no OR-across-columns support in the compat client's filter().
  useEffect(() => {
    if (!contact?.id) return;
    let cancelled = false;
    client.entities.Load.list('-created_date', 500).then(all => {
      if (cancelled) return;
      const matches = all
        .filter(l =>
          l.shipper_contact_id === contact.id ||
          l.consignee_contact_id === contact.id ||
          l.customer_contact_id === contact.id
        )
        .sort((a, b) => new Date(b.pickup_date || b.created_date) - new Date(a.pickup_date || a.created_date));
      setLoadHistory(matches);
    });
    return () => { cancelled = true; };
  }, [contact?.id]);

  const handleDeletePerson = async (person) => {
    if (!window.confirm(`Remove ${person.name}?`)) return;
    await client.entities.Contact.delete(person.id);
    toast.success('Contact removed.');
    onContactsChanged?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-2xl my-8 z-10 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="font-space font-bold text-foreground text-lg">{contact.name}</h2>
            {liveContact.is_blacklisted && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-400">
                <Ban className="w-3 h-3" /> BLACKLISTED
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-6 text-sm">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Type</p>
            <p className="font-semibold text-foreground">{contact.type}</p>
          </div>
          {contact.contact && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Contact Person</p>
              <p className="font-semibold text-foreground">{contact.contact}</p>
            </div>
          )}
          {contact.street && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Street</p>
              <p className="font-semibold text-foreground">{contact.street}</p>
            </div>
          )}
          {contact.city && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">City</p>
              <p className="font-semibold text-foreground">{contact.city}</p>
            </div>
          )}
          {contact.state && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">State</p>
              <p className="font-semibold text-foreground">{contact.state}</p>
            </div>
          )}
          {contact.zip_code && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">ZIP Code</p>
              <p className="font-semibold text-foreground">{contact.zip_code}</p>
            </div>
          )}
          {contact.email && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Email</p>
              <a href={`mailto:${contact.email}`} className="text-blue-400 hover:text-blue-300">
                {contact.email}
              </a>
            </div>
          )}
          {contact.primary_phone && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Primary Phone</p>
              <p className="font-semibold text-foreground">{contact.primary_phone}</p>
            </div>
          )}
          {contact.alternate_phone && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Alternate Phone</p>
              <p className="font-semibold text-foreground">{contact.alternate_phone}</p>
            </div>
          )}
          {contact.alternate_phone2 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Alternate Phone 2</p>
              <p className="font-semibold text-foreground">{contact.alternate_phone2}</p>
            </div>
          )}
          {contact.fax && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Fax</p>
              <p className="font-semibold text-foreground">{contact.fax}</p>
            </div>
          )}
          {contact.motor_carrier_number && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">MC Number</p>
              <p className="font-semibold text-foreground">{contact.motor_carrier_number}</p>
            </div>
          )}
        </div>

        {contact.notes && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">Notes</p>
            <p className="text-sm text-foreground">{contact.notes}</p>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
              Contacts {people.length > 0 && `(${people.length})`}
            </p>
            <button
              onClick={() => { setEditPerson(null); setShowPersonForm(true); }}
              className="text-xs font-medium text-primary hover:text-primary/80 flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Add Contact
            </button>
          </div>
          {people.length === 0 ? (
            <p className="text-sm text-muted-foreground">No contacts added yet.</p>
          ) : (
            <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
              {people.map(p => (
                <div key={p.id} className="flex items-center justify-between px-3 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <User className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {p.name}
                        {p.title && <span className="text-muted-foreground font-normal"> — {p.title}</span>}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[p.primary_phone, p.email].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => { setEditPerson(p); setShowPersonForm(true); }}
                      className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeletePerson(p)}
                      className="p-1 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold mb-2">
            Load History {loadHistory && loadHistory.length > 0 && `(${loadHistory.length})`}
          </p>
          {loadHistory === null ? (
            <div className="flex justify-center py-6">
              <div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
            </div>
          ) : loadHistory.length === 0 ? (
            <p className="text-sm text-muted-foreground">No loads booked with this account yet.</p>
          ) : (
            <>
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border bg-muted/40">
                        <th className="text-left px-3 py-2 text-muted-foreground font-medium">Load #</th>
                        <th className="text-left px-3 py-2 text-muted-foreground font-medium">Pickup</th>
                        <th className="text-left px-3 py-2 text-muted-foreground font-medium">Delivery</th>
                        <th className="text-right px-3 py-2 text-muted-foreground font-medium">Rate</th>
                        <th className="text-left px-3 py-2 text-muted-foreground font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {loadHistory.slice(0, HISTORY_DISPLAY_LIMIT).map(l => (
                        <tr key={l.id}>
                          <td className="px-3 py-2 font-semibold text-foreground flex items-center gap-1.5">
                            <Truck className="w-3 h-3 text-muted-foreground flex-shrink-0" /> #{l.load_number || '—'}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{l.pickup_date || '—'}</td>
                          <td className="px-3 py-2 text-muted-foreground">{l.delivery_date || '—'}</td>
                          <td className="px-3 py-2 text-right font-semibold text-foreground">
                            {l.rate != null ? `$${Number(l.rate).toLocaleString()}` : '—'}
                          </td>
                          <td className="px-3 py-2">
                            <span className={cn(
                              "text-[10px] font-semibold px-2 py-0.5 rounded-full border",
                              STATUS_STYLES[l.status] || 'text-muted-foreground bg-muted/40 border-border'
                            )}>
                              {l.status || '—'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {loadHistory.length > HISTORY_DISPLAY_LIMIT && (
                <p className="text-xs text-muted-foreground mt-1.5">
                  Showing {HISTORY_DISPLAY_LIMIT} most recent of {loadHistory.length} loads.
                </p>
              )}
              <div className="flex justify-between items-center px-3 py-2 mt-1.5 rounded-lg bg-muted/40 border border-border">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Total</span>
                <span className="text-sm font-bold text-foreground">
                  ${loadHistory.reduce((sum, l) => sum + (Number(l.rate) || 0), 0).toLocaleString()}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <Button onClick={onEdit} className="bg-primary text-primary-foreground">
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </Button>
          {staffRole === 'super_admin' && (
            <Button
              onClick={handleToggleBlacklist}
              disabled={blacklisting}
              variant="outline"
              className={liveContact.is_blacklisted
                ? "border-green-500/40 text-green-400 hover:bg-green-500/10"
                : "border-red-500/40 text-red-400 hover:bg-red-500/10"}
            >
              {liveContact.is_blacklisted
                ? <><ShieldCheck className="w-4 h-4 mr-2" /> Remove from Blacklist</>
                : <><Ban className="w-4 h-4 mr-2" /> Blacklist</>}
            </Button>
          )}
          <Button onClick={onClose} variant="outline" className="border-border">
            Close
          </Button>
        </div>
      </div>

      {showPersonForm && (
        <ContactPersonFormModal
          person={editPerson}
          accountId={contact.id}
          onClose={() => { setShowPersonForm(false); setEditPerson(null); }}
          onSaved={() => { setShowPersonForm(false); setEditPerson(null); onContactsChanged?.(); }}
        />
      )}
    </div>
  );
}